import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import { lazy, Suspense } from 'react';
import './App.css';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ImageGenerationPage = lazy(() => import('./pages/ImageGenerationPage'));
const VideoGenerationPage = lazy(() => import('./pages/VideoGenerationPage'));
const ImageToVideoPage = lazy(() => import('./pages/ImageToVideoPage'));
const VideoEditPage = lazy(() => import('./pages/VideoEditPage'));
const AmazonAPlusPage = lazy(() => import('./pages/AmazonAPlusPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const BatchQueuePage = lazy(() => import('./pages/BatchQueuePage'));
const UserAccessPage = lazy(() => import('./pages/UserAccessPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

const PageLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/welcome" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/welcome" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="generate/image" element={<ImageGenerationPage />} />
              <Route path="generate/video" element={<VideoGenerationPage />} />
              <Route path="generate/image-to-video" element={<ImageToVideoPage />} />
              <Route path="generate/video-edit" element={<VideoEditPage />} />
              <Route path="generate/amazon-aplus" element={<AmazonAPlusPage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="batch" element={<BatchQueuePage />} />
              <Route path="admin/users" element={<ProtectedRoute adminOnly><UserAccessPage /></ProtectedRoute>} />
              <Route path="admin/dashboard" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/welcome" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#0f172a',
            border: '1px solid #1e293b',
            color: '#f8fafc',
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
